import { ACTING_ROLE_LABELS, useActingRole } from '@brush/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../queries';
import { roleChoicesFor } from '../roles';
import { themeFor } from '../theme';

/**
 * A way back to the role you set aside, on the screens that have no chrome to carry one.
 *
 * <b>It exists because the acting role stopped being a detail and became the way in.</b> Staff
 * browse and players read their own card, and which you are is decided by the role in force
 * (`player.tsx`) — so an operator who wants their own portal has to switch to `player`, and a
 * player who is also staff has to switch back to look somebody up. `PortalShell` has carried that
 * switcher since ADR 0009; the shell-less screens — the staff picker, and the cards screen — had
 * no way out at all once a choice was stored, because `RoleChooser` appears only while none is.
 *
 * Switching is `clear()` rather than `choose()`: with the choice gone the chooser is what renders,
 * which is one screen deciding this rather than two that can word it differently.
 *
 * Chalk, never signal orange. Orange means "this is you" in the portal and is never a control.
 */
export function RoleSwitch({ notice }: { notice?: string | null }) {
  const me = useCurrentUser();
  const { actingRole, clear } = useActingRole();
  const queryClient = useQueryClient();
  const theme = themeFor(null);

  // Offered only where there is something to switch between — the same rule the chooser follows.
  // For the great majority of players this component renders nothing at all.
  const choices = roleChoicesFor(me.data?.availableRoles ?? []);
  if (choices.length < 2) return null;

  return (
    <div className="mt-6 border-t pt-4" style={{ borderColor: theme.border }}>
      {notice ? (
        <p className="text-xs" style={{ color: theme.textMuted }}>
          {notice}
        </p>
      ) : null}
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-xs" style={{ color: theme.textMuted }}>
          {actingRole ? `Acting as ${ACTING_ROLE_LABELS[actingRole].toLowerCase()}.` : null}
        </span>
        <button
          type="button"
          onClick={() => {
            clear();
            // What was cached was read as somebody else — and in this app that includes whose
            // portal is on screen.
            queryClient.clear();
          }}
          className="shrink-0 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] transition hover:text-white"
          style={{ border: `1px solid ${theme.border}`, color: theme.textMuted }}
        >
          Switch role
        </button>
      </div>
    </div>
  );
}
